import { withDependencies, named } from '@wix/thunderbolt-ioc'
import type { BrowserWindow, IPropsStore, Experiments, INavigationManager } from '@wix/thunderbolt-symbols'
import {
	BrowserWindowSymbol,
	FeatureStateSymbol,
	PageFeatureConfigSymbol,
	Props,
	PageTransitionsCompletedSymbol,
	ExperimentsSymbol,
	NavigationManagerSymbol,
} from '@wix/thunderbolt-symbols'
import type { PageTransitionsPageState, PageTransitionsPageConfig } from './types'
import { name } from './symbols'
import type { ComponentWillMount, ViewerComponent } from 'feature-components'
import type { IFeatureState } from 'thunderbolt-feature-state'
import type { IPageTransitionsCompleted } from './IPageTransitionsCompleted'
import type { IScrollRestorationAPI } from 'feature-scroll-restoration'
import { ScrollRestorationAPISymbol } from 'feature-scroll-restoration'

export const PageComponentTransitionsWillMount = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		Props,
		PageTransitionsCompletedSymbol,
		ScrollRestorationAPISymbol,
		named(FeatureStateSymbol, name),
		BrowserWindowSymbol,
		ExperimentsSymbol,
		NavigationManagerSymbol,
	],
	(
		pageConfig: PageTransitionsPageConfig,
		propsStore: IPropsStore,
		pageTransitionsCompleted: IPageTransitionsCompleted,
		scrollRestorationAPI: IScrollRestorationAPI,
		featureState: IFeatureState<PageTransitionsPageState>,
		browserWindow: BrowserWindow,
		experiments: Experiments,
		navigationManager: INavigationManager
	): ComponentWillMount<ViewerComponent> => {
		return {
			componentTypes: ['Page'],
			componentWillMount(pageComponent) {
				const state = featureState.get()
				const transitionEnabled = state ? state.nextTransitionEnabled : true
				const isFirstMount = state ? state.isFirstMount : true
				const supportsViewTransition = browserWindow ? 'startViewTransition' in browserWindow.document : false
				const useViewTransition = supportsViewTransition && pageConfig.shouldUseViewTransition

				const pageId = pageComponent.id

				propsStore.update({
					SITE_PAGES: {
						transitionEnabled,
						...(useViewTransition
							? {}
							: {
									onTransitionStarting: () => {
										if (!scrollRestorationAPI.getScrollYHistoryState()) {
											scrollRestorationAPI.scrollToTop(
												experiments['specs.thunderbolt.pageTransitionScrollSmoothly'] ? 'smooth' : undefined
											)
										}
										if (experiments['specs.thunderbolt.postTransitionElementFocus']) {
											if (navigationManager.isFirstPage()) {
												const targetElement = window!.document.getElementById('SCROLL_TO_TOP')
												targetElement?.focus()
											} else {
												const firstSection = window.document.querySelector('main section') as HTMLElement
												firstSection?.focus({ preventScroll: true })
											}
										}
									},
									onTransitionComplete: () => {
										pageTransitionsCompleted.notifyPageTransitionsCompleted(pageId)
										if (scrollRestorationAPI.getScrollYHistoryState()) {
											scrollRestorationAPI.restoreScrollPosition()
										}
									},
								}),
					},
				})

				featureState.update(() => ({
					...state,
					isFirstMount,
					nextTransitionEnabled: true,
				}))
			},
		}
	}
)
